import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  LocalShipping,
  People,
  PeopleAlt,
  Receipt,
  LocalGasStation,
  Build,
  BarChart,
  AccountCircle,
  ExitToApp,
  Store,
  Business,
  Lock,
  BusinessCenter,
  AccountTree,
  Straighten,
  Image as ImageIcon,
  Folder,
  ExpandLess,
  ExpandMore,
  Settings,
} from '@mui/icons-material';

const DRAWER_WIDTH = 240;

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [senhaOpen, setSenhaOpen] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [erros, setErros] = useState<{ senhaAtual?: string; novaSenha?: string; confirmarSenha?: string }>({});
  const [salvando, setSalvando] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const allMenuItems: any[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard', roles: ['ADMIN', 'GERENTE'] },
    { text: 'Abastecimentos', icon: <LocalGasStation />, path: '/abastecimentos', roles: ['ADMIN', 'GERENTE', 'MOTORISTA'] },
    {
      text: 'Cadastro', icon: <Folder />, roles: ['ADMIN', 'GERENTE', 'SUPER_ADMIN'],
      children: [
        { text: 'Departamentos', icon: <AccountTree />, path: '/departamentos', roles: ['ADMIN', 'GERENTE'] },
        { text: 'Motoristas', icon: <People />, path: '/motoristas', roles: ['ADMIN', 'GERENTE'] },
        { text: 'Postos', icon: <Store />, path: '/postos', roles: ['ADMIN', 'GERENTE'] },
        { text: 'Preços Combustível', icon: <LocalGasStation />, path: '/precos-combustivel', roles: ['ADMIN', 'GERENTE'] },
        { text: 'Usuários', icon: <PeopleAlt />, path: '/usuarios', roles: ['ADMIN', 'GERENTE', 'SUPER_ADMIN'] },
        { text: 'Veículos', icon: <LocalShipping />, path: '/veiculos', roles: ['ADMIN', 'GERENTE'] },
      ],
    },
    {
      text: 'Configurações', icon: <Settings />, roles: ['ADMIN'],
      children: [
        { text: 'Imagens', icon: <ImageIcon />, path: '/imagens', roles: ['ADMIN'] },
        { text: 'Margens', icon: <Straighten />, path: '/margens', roles: ['ADMIN'] },
      ],
    },
    { text: 'Fechamentos', icon: <Receipt />, path: '/fechamentos', roles: ['ADMIN', 'GERENTE'] },
    { text: 'Manutenções', icon: <Build />, path: '/manutencoes', roles: ['ADMIN', 'GERENTE', 'MOTORISTA'] },
    { text: 'Organizações', icon: <Business />, path: '/tenants', roles: ['SUPER_ADMIN'] },
    { text: 'Relatórios', icon: <BarChart />, path: '/relatorios', roles: ['ADMIN', 'GERENTE'] },
    { text: 'Solicitações', icon: <Receipt />, path: '/solicitacoes', roles: ['ADMIN', 'GERENTE', 'MOTORISTA'] },
  ];

  const menuItems = allMenuItems
    .map((item) => {
      if (item.children) {
        const visibleChildren = item.children.filter((c: any) => user?.perfil && c.roles.includes(user.perfil));
        if (visibleChildren.length === 0) return null;
        return { ...item, children: visibleChildren };
      }
      if (user?.perfil && item.roles.includes(user.perfil)) return item;
      return null;
    })
    .filter(Boolean);

  useEffect(() => {
    menuItems.forEach((item: any) => {
      if (item?.children && item.children.some((c: any) => c.path === location.pathname)) {
        setOpenGroups((prev) => (prev[item.text] ? prev : { ...prev, [item.text]: true }));
      }
    });
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  const handleOpenSenha = () => {
    handleClose();
    setSenhaAtual('');
    setNovaSenha('');
    setConfirmarSenha('');
    setErros({});
    setSenhaOpen(true);
  };

  const handleSalvarSenha = async () => {
    const novosErros: typeof erros = {};
    if (!senhaAtual) novosErros.senhaAtual = 'Senha atual é obrigatória';
    if (!novaSenha) novosErros.novaSenha = 'Nova senha é obrigatória';
    else if (novaSenha.length < 6) novosErros.novaSenha = 'Mínimo 6 caracteres';
    if (novaSenha !== confirmarSenha) novosErros.confirmarSenha = 'Senhas não conferem';

    if (Object.keys(novosErros).length > 0) {
      setErros(novosErros);
      return;
    }

    setSalvando(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ senhaAtual, novaSenha }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Erro ao alterar senha');
      }
      setSenhaOpen(false);
    } catch (err: any) {
      setErros({ senhaAtual: err.message });
    } finally {
      setSalvando(false);
    }
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 1, py: 0.5, textAlign: 'center', pt: 7, flexShrink: 0 }}>
        <img src="/images/logo.png" alt="GFrotas" style={{ height: 56, marginBottom: 2 }} />
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mt: 0.5, display: 'block' }}>
          {user?.perfil}
        </Typography>
      </Box>
      <List sx={{ overflow: 'auto', flex: 1, py: 0 }}>
        {menuItems.map((item: any) => {
          if (item.children) {
            const isOpen = openGroups[item.text] ?? item.children.some((c: any) => c.path === location.pathname);
            return (
              <React.Fragment key={item.text}>
                <ListItem
                  onClick={() => setOpenGroups((prev) => ({ ...prev, [item.text]: !isOpen }))}
                  sx={{ cursor: 'pointer', py: 0.5 }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {isOpen ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child: any) => (
                      <ListItem
                        key={child.text}
                        onClick={() => {
                          navigate(child.path);
                          if (isMobile) setMobileOpen(false);
                        }}
                        sx={{
                          cursor: 'pointer',
                          py: 0.5,
                          pl: 4,
                          bgcolor: location.pathname === child.path ? 'action.selected' : 'transparent',
                        }}
                      >
                        <ListItemIcon>{child.icon}</ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }
          return (
            <ListItem
              key={item.text}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                cursor: 'pointer',
                py: 0.5,
                bgcolor: location.pathname === item.path ? 'action.selected' : 'transparent',
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img src="/images/gfrotas.png" alt="GFrotas" style={{ height: 28 }} />
            {user?.tenantNome && user.perfil !== 'SUPER_ADMIN' && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} />
                <Typography variant="subtitle1" noWrap>{user.tenantNome}</Typography>
              </>
            )}
          </Box>
          <IconButton color="inherit" onClick={handleMenu}>
            <AccountCircle />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.nome}</Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            {user?.tenants && user.tenants.length > 1 && (
              <MenuItem onClick={() => { handleClose(); navigate('/selecionar-tenant'); }}>
                <ListItemIcon><BusinessCenter /></ListItemIcon>
                Trocar organização
              </MenuItem>
            )}
            <MenuItem onClick={handleOpenSenha}>
              <ListItemIcon><Lock /></ListItemIcon>
              Alterar senha
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><ExitToApp /></ListItemIcon>
              Sair
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { xs: '100%', sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          py: 2,
          px: 1.5,
          mt: '52px',
        }}
      >
        <Outlet />
      </Box>
      <Dialog open={senhaOpen} onClose={() => setSenhaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Alterar Senha</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Senha Atual"
            type="password"
            value={senhaAtual}
            onChange={(e) => { setSenhaAtual(e.target.value); setErros((prev) => ({ ...prev, senhaAtual: undefined })); }}
            error={!!erros.senhaAtual}
            helperText={erros.senhaAtual}
            fullWidth
          />
          <TextField
            label="Nova Senha"
            type="password"
            value={novaSenha}
            onChange={(e) => { setNovaSenha(e.target.value); setErros((prev) => ({ ...prev, novaSenha: undefined })); }}
            error={!!erros.novaSenha}
            helperText={erros.novaSenha}
            fullWidth
          />
          <TextField
            label="Confirmar Nova Senha"
            type="password"
            value={confirmarSenha}
            onChange={(e) => { setConfirmarSenha(e.target.value); setErros((prev) => ({ ...prev, confirmarSenha: undefined })); }}
            error={!!erros.confirmarSenha}
            helperText={erros.confirmarSenha}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSenhaOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSalvarSenha} disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
